import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, ChevronDown, Menu, X, Settings, CreditCard } from "lucide-react";
import Logo from "@/components/nav/Logo";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Single shared header for the entire app. Three modes:
 *
 *   mode="public"     — Landing / Pricing / Login. Logo → "/", right-side
 *                       guest CTAs (Тарифи / Увійти) or "Кабінет →" for authed.
 *   mode="onboarding" — Step1/2/3 + LaunchWizard. Logo → /dashboard, vertical
 *                       progress (1—2—3) and an exit button.
 *   mode="app"        — Dashboard, Reports, Strategy, Notifications, Settings.
 *                       Top nav links, NotificationBell, profile dropdown,
 *                       and burger for mobile sidebar.
 */
const ONB_STEPS = [
    { n: 1, label: "Google Ads" },
    { n: 2, label: "Бізнес" },
    { n: 3, label: "Стратегія" },
];

export default function AppHeader({ mode = "app", currentStep, onToggleSidebar }) {
    if (mode === "public")     return <PublicHeader />;
    if (mode === "onboarding") return <OnboardingHeader currentStep={currentStep} />;
    return <AppNavHeader onToggleSidebar={onToggleSidebar} />;
}


// ─── PUBLIC ────────────────────────────────────────────────────────────────
function PublicHeader() {
    const { user } = useAuth();
    return (
        <header className="border-b border-border bg-background/70 backdrop-blur-md sticky top-0 z-40" data-testid="public-header">
            <div className="container-mx flex items-center justify-between h-[68px]">
                <Logo to="/" testid="public-logo" />
                <nav className="flex items-center gap-3 sm:gap-4">
                    <Link to="/pricing" className="text-sm text-secondary-c hover:text-text-primary transition" data-testid="public-pricing-link">
                        Тарифи
                    </Link>
                    {user ? (
                        <Link to="/dashboard" className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition" data-testid="public-cabinet-cta">
                            Кабінет →
                        </Link>
                    ) : (
                        <Link to="/login" className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition" data-testid="public-login-cta">
                            Увійти
                        </Link>
                    )}
                </nav>
            </div>
        </header>
    );
}


// ─── ONBOARDING ────────────────────────────────────────────────────────────
function OnboardingHeader({ currentStep = 1 }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    return (
        <header className="border-b border-border" data-testid="onboarding-header">
            <div className="container-mx flex items-center justify-between h-[68px]">
                <Logo to="/dashboard" testid="onb-logo" />

                <ol className="hidden md:flex items-center gap-3" data-testid="onb-progress">
                    {ONB_STEPS.map((s, i) => {
                        const done = currentStep > s.n;
                        const active = currentStep === s.n;
                        return (
                            <li key={s.n} className="flex items-center gap-2">
                                <div
                                    className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-mono font-semibold ${
                                        done ? "bg-success text-background"
                                          : active ? "bg-primary text-white pulse-glow"
                                          : "bg-surface-hover text-secondary-c border border-border"
                                    }`}
                                    data-testid={`onb-step-${s.n}`}
                                >{done ? "✓" : s.n}</div>
                                <span className={`text-xs font-medium ${active ? "text-text-primary" : "text-secondary-c"}`}>{s.label}</span>
                                {s.n < 3 && <span className="w-6 h-px bg-border ml-1" />}
                            </li>
                        );
                    })}
                </ol>

                <div className="flex items-center gap-3">
                    <span className="text-xs text-secondary-c hidden sm:inline truncate max-w-[180px]" title={user?.email}>{user?.email}</span>
                    <button
                        onClick={async () => { await logout(); navigate("/"); }}
                        className="text-secondary-c hover:text-text-primary p-2"
                        data-testid="onb-logout-btn"
                        title="Вийти"
                    ><LogOut size={16} /></button>
                </div>
            </div>
        </header>
    );
}


// ─── APP (authed) ──────────────────────────────────────────────────────────
function AppNavHeader({ onToggleSidebar }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // Close profile menu on route change.
    useEffect(() => { setMenuOpen(false); }, [location.pathname]);

    // Close on click-outside — match NotificationBell's behavior so the menu
    // feels consistent and doesn't hang open when the user clicks elsewhere.
    useEffect(() => {
        if (!menuOpen) return;
        const onDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [menuOpen]);

    // Close on Escape — accessibility nicety, mirrors most dropdown patterns.
    useEffect(() => {
        if (!menuOpen) return;
        const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [menuOpen]);

    return (
        <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40" data-testid="app-header">
            <div className="container-mx flex items-center justify-between h-[68px] gap-4">
                <div className="flex items-center gap-3">
                    {onToggleSidebar && (
                        <button
                            onClick={onToggleSidebar}
                            className="lg:hidden p-2 -ml-2 text-secondary-c hover:text-text-primary"
                            data-testid="app-burger-btn"
                            aria-label="Меню"
                        ><Menu size={18} /></button>
                    )}
                    <Logo to="/dashboard" testid="app-logo" />
                </div>



                <div className="flex items-center gap-3 sm:gap-4">
                    <NotificationBell />
                    <div className="relative" ref={menuRef}>
                        <button
                            onClick={() => setMenuOpen(v => !v)}
                            className="flex items-center gap-2 text-sm text-text-primary"
                            data-testid="app-profile-btn"
                            aria-label="Профіль"
                        >
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-mono font-bold">
                                {(user?.name || user?.email || "?")[0].toUpperCase()}
                            </div>
                            <ChevronDown size={14} className="text-secondary-c hidden sm:block" />
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 top-12 dropdown-panel rounded-xl p-2 w-[240px] z-50" data-testid="app-profile-menu">
                                <div className="px-3 py-2 border-b border-border">
                                    <div className="text-sm text-text-primary font-medium truncate">{user?.name || "—"}</div>
                                    <div className="text-xs text-secondary-c truncate">{user?.email}</div>
                                </div>
                                <ProfileMenuItem icon={Settings}    onClick={() => navigate("/settings")} testid="app-menu-settings">Налаштування</ProfileMenuItem>
                                <ProfileMenuItem icon={CreditCard}  onClick={() => navigate("/pricing")}  testid="app-menu-pricing">Тарифи та білінг</ProfileMenuItem>
                                <div className="my-1 h-px bg-border" />
                                <ProfileMenuItem icon={LogOut} danger onClick={async () => { await logout(); navigate("/"); }} testid="app-logout-btn">Вийти</ProfileMenuItem>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}

function ProfileMenuItem({ icon: Icon, children, onClick, testid, danger }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-surface-hover transition flex items-center gap-2 ${
                danger ? "text-secondary-c hover:text-error-c" : "text-secondary-c hover:text-text-primary"}`}
        >
            <Icon size={14} /> {children}
        </button>
    );
}

// Re-export the X icon as a separate utility for callers needing it.
export { X };